import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmationDialog from "./ConfirmationDialog";

/**
 * 🗑️ DeleteButton Component
 * 
 * Modern ve şık görünümlü silme butonu component'i
 * ConfirmationDialog ile entegre, farklı sayfalarda kullanılabilir
 * 
 * @param {string} label - Buton üzerinde gösterilecek metin (default: "Delete")
 * @param {function} onConfirm - Onaylandığında çalışacak fonksiyon
 * @param {string} confirmationTitle - Dialog başlığı
 * @param {string} confirmationDescription - Dialog açıklama metni
 * @param {string} variant - Buton boyutu: "small" | "medium" | "large" (default: "medium")
 * @param {boolean} disabled - Buton disabled durumu
 * @param {boolean} loading - Loading durumu
 * @param {ReactNode} icon - Custom icon (opsiyonel)
 * @param {object} sx - Custom styling (opsiyonel)
 * @param {string} size - Material-UI size prop (default: "medium")
 */
export default function DeleteButton({
  label = "Delete",
  onConfirm,
  confirmationTitle = "Confirm Deletion",
  confirmationDescription = "Are you sure you want to delete this item? This action cannot be undone.",
  variant = "medium",
  disabled = false,
  loading = false,
  icon,
  sx = {},
  size = "medium",
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Variant'a göre boyut ayarları
  const variantStyles = {
    small: {
      px: 2,
      py: 0.75,
      fontSize: "0.8rem",
      iconSize: 18,
    },
    medium: {
      px: 3,
      py: 1.3,
      fontSize: "0.95rem",
      iconSize: 22,
    },
    large: {
      px: 4,
      py: 1.5,
      fontSize: "1.1rem",
      iconSize: 24,
    },
  };

  const currentVariant = variantStyles[variant] || variantStyles.medium;

  const handleClick = () => {
    setDialogOpen(true);
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled || loading}
        size={size}
        startIcon={
          icon || (
            <DeleteOutlineIcon sx={{ fontSize: currentVariant.iconSize }} />
          )
        }
        sx={{
          color: colors.grey[100],
          background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.redAccent[500]} 100%)`,
          fontWeight: 700,
          borderRadius: 3,
          px: currentVariant.px,
          py: currentVariant.py,
          fontSize: currentVariant.fontSize,
          textTransform: "none",
          letterSpacing: 0.4,
          boxShadow: `0 12px 28px ${colors.redAccent[900]}45`,
          transition: "all 0.25s ease",
          "&:hover": {
            transform: "translateY(-3px) scale(1.02)",
            boxShadow: `0 18px 36px ${colors.redAccent[900]}60`,
            background: `linear-gradient(135deg, ${colors.redAccent[700]} 0%, ${colors.redAccent[500]} 100%)`,
          },
          "&:active": {
            transform: "translateY(-1px) scale(1.01)",
          },
          "&:disabled": {
            background: `${colors.grey[700]}40`,
            color: colors.grey[500],
            boxShadow: "none",
            transform: "none",
          },
          ...sx,
        }}
      >
        {loading ? "Deleting..." : label}
      </Button>

      <ConfirmationDialog
        open={dialogOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmationTitle}
        description={confirmationDescription}
      />
    </>
  );
}

